import { trpc } from "../lib/trpc.js";
import { useAuth } from "../hooks/useAuth.js";
import {
  DollarSign, Globe, BrainCircuit, Activity, TrendingUp, Users,
  ArrowUpRight, ArrowDownRight, Workflow, BarChart3
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from "recharts";

function MetricCard({ label, value, subtitle, change, icon: Icon }: {
  label: string;
  value: string;
  subtitle?: string;
  change?: number;
  icon: typeof DollarSign;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-primary" />
        </div>
      </div>
      <div className="text-2xl font-bold text-foreground tracking-tight">{value}</div>
      {subtitle && (
        <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
      )}
      {change !== undefined && (
        <div className={`flex items-center gap-1 text-xs mt-1.5 ${change >= 0 ? "text-success" : "text-red-500"}`}>
          {change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {change >= 0 ? "+" : ""}{change}% vs mês anterior
        </div>
      )}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
        <p className="text-xs font-medium text-foreground mb-1">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-xs" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === "number" ? entry.value.toLocaleString("pt-BR") : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function Dashboard() {
  const { user } = useAuth();
  const { data: metrics, isLoading } = trpc.dashboard.metrics.useQuery();
  const { data: chartData } = trpc.dashboard.chartData.useQuery();

  const formatCurrency = (value: number) => {
    return `R$ ${(value / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Bem-vindo, {user?.name || "Operador"}. Aqui está seu panorama geral.
        </p>
      </div>

      {/* Metrics Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
              <div className="h-4 bg-muted rounded w-24 mb-3" />
              <div className="h-7 bg-muted rounded w-20" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Receita Total"
            value={formatCurrency(metrics?.totalRevenue || 0)}
            change={metrics?.revenueChange}
            icon={DollarSign}
          />
          <MetricCard
            label="Tráfego Total"
            value={(metrics?.totalTraffic || 0).toLocaleString("pt-BR")}
            change={metrics?.trafficChange}
            icon={Activity}
          />
          <MetricCard
            label="Sites Ativos"
            value={`${metrics?.activeSites || 0}/${metrics?.totalSites || 0}`}
            subtitle="sites operacionais"
            icon={Globe}
          />
          <MetricCard
            label="Agentes Ativos"
            value={`${metrics?.activeAgents || 0}/${metrics?.totalAgents || 0}`}
            subtitle="agentes online"
            icon={BrainCircuit}
          />
        </div>
      )}

      {/* Secondary Metrics */}
      {!isLoading && metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <TrendingUp className="w-3 h-3" />
              CPC Médio
            </div>
            <p className="text-lg font-semibold text-foreground">
              R$ {((metrics.avgCpc || 0) / 100).toFixed(2)}
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Workflow className="w-3 h-3" />
              Pipelines
            </div>
            <p className="text-lg font-semibold text-foreground">{metrics.totalPipelines}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <BarChart3 className="w-3 h-3" />
              Brain Tasks
            </div>
            <p className="text-lg font-semibold text-foreground">{metrics.pendingBrainTasks}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Users className="w-3 h-3" />
              Execuções Recentes
            </div>
            <p className="text-lg font-semibold text-foreground">{metrics.recentExecutions?.length || 0}</p>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue Chart */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Receita vs Gasto (7 dias)</h3>
          <div className="h-64">
            {chartData?.revenueData ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData.revenueData}>
                  <defs>
                    <linearGradient id="colorReceita" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorGasto" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="day" tick={{ fill: "var(--color-muted-foreground)", fontSize: 12 }} />
                  <YAxis tick={{ fill: "var(--color-muted-foreground)", fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: "12px" }}
                    formatter={(value: string) => <span style={{ color: "var(--color-muted-foreground)" }}>{value}</span>}
                  />
                  <Area
                    type="monotone"
                    dataKey="receita"
                    name="Receita"
                    stroke="#10b981"
                    fillOpacity={1}
                    fill="url(#colorReceita)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="gasto"
                    name="Gasto"
                    stroke="#ef4444"
                    fillOpacity={1}
                    fill="url(#colorGasto)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                Carregando dados...
              </div>
            )}
          </div>
        </div>

        {/* Traffic Chart */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Tráfego por Origem (7 dias)</h3>
          <div className="h-64">
            {chartData?.trafficData ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData.trafficData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="day" tick={{ fill: "var(--color-muted-foreground)", fontSize: 12 }} />
                  <YAxis tick={{ fill: "var(--color-muted-foreground)", fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: "12px" }}
                    formatter={(value: string) => <span style={{ color: "var(--color-muted-foreground)" }}>{value}</span>}
                  />
                  <Bar dataKey="organico" name="Orgânico" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pago" name="Pago" fill="#34d399" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                Carregando dados...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity & Sites */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Executions */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Execuções Recentes</h3>
          {metrics?.recentExecutions && metrics.recentExecutions.length > 0 ? (
            <div className="space-y-2">
              {metrics.recentExecutions.map((exec) => (
                <div key={exec.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <p className="text-sm text-foreground">Execução #{exec.id.slice(0, 8)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(exec.createdAt).toLocaleString("pt-BR")}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    exec.status === "completed" ? "bg-success/10 text-success" :
                    exec.status === "running" ? "bg-primary/10 text-primary" :
                    exec.status === "failed" ? "bg-red-500/10 text-red-400" :
                    "bg-muted text-muted-foreground"
                  }`}>
                    {exec.status === "completed" ? "Concluído" :
                     exec.status === "running" ? "Executando" :
                     exec.status === "failed" ? "Falhou" :
                     exec.status === "queued" ? "Na Fila" : exec.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Nenhuma execução recente. Inicie um pipeline para ver a atividade aqui.
            </p>
          )}
        </div>

        {/* Recent Sites */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Sites Recentes</h3>
          {metrics?.recentSites && metrics.recentSites.length > 0 ? (
            <div className="space-y-2">
              {metrics.recentSites.map((site) => (
                <div key={site.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <p className="text-sm text-foreground">{site.name}</p>
                    <p className="text-xs text-muted-foreground">{site.domain || "Sem domínio"}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    site.status === "active" ? "bg-success/10 text-success" :
                    site.status === "building" ? "bg-primary/10 text-primary" :
                    site.status === "error" ? "bg-red-500/10 text-red-400" :
                    "bg-muted text-muted-foreground"
                  }`}>
                    {site.status === "active" ? "Ativo" :
                     site.status === "building" ? "Construindo" :
                     site.status === "paused" ? "Pausado" :
                     site.status === "error" ? "Erro" : site.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Nenhum site registrado ainda.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
